/**
 * `ProfileEditRow` — the shared `<dd>` chrome for ONE inline-editable field in
 * a `<dl className="kvs">` account panel: a value + hover-Edit in the read
 * view, an editor + Save/Cancel + error line in the edit view.
 *
 * Lived privately inside `MemberDetail.tsx` until 2026-08-19, when
 * `/trainers/:id` needed the identical row to host the NEW
 * `change_trainer_email` action. Extracted rather than copied — two divergent
 * copies of the same chrome is how the member and trainer panels start looking
 * like different products.
 *
 * DISTINCT from `InlineFieldEditor`, and the difference is the markup contract,
 * not the behaviour: that component renders its OWN `<div className="profile-field">`
 * with an inline `<strong>{label}:</strong>`, which suits the `/pr/people/:id`
 * and `/inbox/threads/:id` person cards. This one renders a bare `<dd>` and
 * carries no label of its own, because it sits in a definition list whose
 * `<dt>` already names the field — dropping the other component in here would
 * print the label twice.
 *
 * Deliberately dumb: it owns no dispatch and no draft state. The caller owns
 * both, so each field can pick its own action, input type and validation (see
 * `MemberEmailEditor` / `TrainerEmailEditor`).
 */

import type { JSX, ReactNode } from "react";

export function ProfileEditRow({
  editing,
  onEdit,
  display,
  editor,
  onSave,
  onCancel,
  loading,
  error,
  errorDetail,
}: {
  readonly editing: boolean;
  readonly onEdit: () => void;
  readonly display: ReactNode;
  readonly editor: ReactNode;
  readonly onSave: () => void;
  readonly onCancel: () => void;
  readonly loading: boolean;
  readonly error: string | null;
  /**
   * Shown under the error line, e.g. the member page's "Open the other
   * member →" link when a number or handle is already on another member
   * (update-member-refs, 2026-11-01).
   */
  readonly errorDetail?: ReactNode;
}): JSX.Element {
  if (!editing) {
    return (
      <dd className="profile-field">
        <span className="profile-field-val">{display}</span>
        <button
          type="button"
          className="profile-field-edit"
          title="Edit"
          onClick={onEdit}
        >
          Edit
        </button>
      </dd>
    );
  }
  return (
    <dd className="profile-field profile-field-editing">
      <div className="profile-edit-row">
        {editor}
        <button
          type="button"
          className="action-dispatch-btn profile-field-save"
          disabled={loading}
          onClick={onSave}
        >
          {loading ? "…" : "Save"}
        </button>
        <button
          type="button"
          className="profile-field-cancel"
          disabled={loading}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
      {error !== null ? (
        <div className="flag-card-err">
          {error}
          {errorDetail}
        </div>
      ) : null}
    </dd>
  );
}
